// @greenlight-spec: 16-api-conventions.md
// @greenlight-spec: 28-it-dashboard-ui.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { GreenlightErrorResponseSchema, AppLifecycleSettingsSchema, BillingSessionSchema, BillingSettingsSchema, BootstrapPluginMarketplaceBodySchema, InstallContactSchema, OrgSettingsSchema, UpdateOrgSettingsSchema, UpdateAppDiscoverabilitySettingsSchema, UpdateFeedbackSharingSettingsSchema, UpdateCrashLogSharingSettingsSchema, UpdateFeedbackObserverReportsSettingsSchema, UpdateAgentTokenUsageReportsSettingsSchema, UpdateGroupNameResolutionSchema, UpdateAppLifecycleSettingsSchema, SetCursorAdminKeySchema, SetSlackWebhookSchema, UpdateNotificationsSettingsSchema, TestIdentityResultSchema, SourceControlVerifyResultSchema, RecreateGithubAppBodySchema, RecreateGithubAppResultSchema, ScmMigrationPreviewSchema, ExecuteScmMigrationBodySchema, ScmMigrationResultSchema, PluginMarketplaceBootstrapResultSchema, PluginMarketplaceIdParamsSchema, PluginMarketplaceUnregisterResultSchema, } from '@greenlight/shared';
import { ReconfigureIdentityBodySchema, TestIdentityBodySchema } from './install.js';
import { errorResponses } from './common.js';
const c = initContract();
export const settingsContract = c.router({
    get: {
        method: 'GET',
        path: '/api/settings',
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Read the full org settings payload (role-gated sections returned as present).',
    },
    updateOrg: {
        method: 'PATCH',
        path: '/api/settings/org',
        body: UpdateOrgSettingsSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Update the org display name (install-set domain fields are read-only).',
    },
    updateContact: {
        method: 'PATCH',
        path: '/api/settings/contact',
        body: InstallContactSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: "Change the administrator Shift contacts about this install's subscription and billing (docs/12 §Install contact). Forwarded to the fleet monitor on the next reporting tick; org_admin only.",
    },
    getBilling: {
        method: 'GET',
        path: '/api/settings/billing',
        responses: {
            200: BillingSettingsSchema,
            ...errorResponses,
        },
        summary: "Read this install's billing summary from Shift's fleet monitor: entitlement, account standing, terms, last settlement, measured usage, and whether hosted payment management is offered (docs/28 §/settings Billing). org_admin only.",
    },
    requestBillingSession: {
        method: 'POST',
        path: '/api/settings/billing/session',
        body: z.object({}).strict(),
        responses: {
            200: BillingSessionSchema,
            ...errorResponses,
            503: GreenlightErrorResponseSchema,
        },
        summary: 'Request a short-lived provider-hosted page to save or manage the payment method (docs/27 §Direct-contract billing). The reporting key stays server-side; org_admin only, audited, rate-limited.',
    },
    updateAppDiscoverability: {
        method: 'PATCH',
        path: '/api/settings/app-discoverability',
        body: UpdateAppDiscoverabilitySettingsSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Set whether a newly registered app starts open to the org or restricted to the people its owner names (docs/42 §Default app access). Ships restricted; existing apps untouched; org_admin only.',
    },
    updateFeedbackSharing: {
        method: 'PATCH',
        path: '/api/settings/feedback-sharing',
        body: UpdateFeedbackSharingSettingsSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Set whether platform feedback is sent to Shift automatically (docs/27). Sends the title and body of new items out of the install; ships off and the change is audited; org_admin only.',
    },
    updateCrashLogSharing: {
        method: 'PATCH',
        path: '/api/settings/crash-log-sharing',
        body: UpdateCrashLogSharingSettingsSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Set whether app crash logs are sent to Shift automatically (ADR-0069). Sends the redacted output of a crashed app container out of the install; ships off and the change is audited; org_admin only.',
    },
    updateFeedbackObserverReports: {
        method: 'PATCH',
        path: '/api/settings/feedback-observer-reports',
        body: UpdateFeedbackObserverReportsSettingsSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: "Set whether the Claude Code plugin's observer hook files platform feedback for this org (docs/20). The hook reads builder sessions locally and files a summary into the org's own /feedback; ships off and the change is audited; org_admin only.",
    },
    updateAgentTokenUsageReports: {
        method: 'PATCH',
        path: '/api/settings/agent-token-usage-reports',
        body: UpdateAgentTokenUsageReportsSettingsSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: "Set whether the Greenlight plugin sends each build session's token counts to this install, so /cost can show token cost per app (docs/11). Counts per model and app only; nothing goes to Shift; ships off and the change is audited; org_admin only.",
    },
    setCursorAdminKey: {
        method: 'PUT',
        path: '/api/settings/agent-token-usage/cursor-admin-key',
        body: SetCursorAdminKeySchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
            422: GreenlightErrorResponseSchema,
        },
        summary: "Store the Cursor team Admin API key the worker uses to read Cursor sessions' token counts (docs/11). Checked against Cursor before it is stored; write-only, held in the vault, never returned; audited without the key; org_admin only.",
    },
    deleteCursorAdminKey: {
        method: 'DELETE',
        path: '/api/settings/agent-token-usage/cursor-admin-key',
        body: c.noBody(),
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Forget the Cursor Admin API key (audited; org_admin only). Cursor sessions stop being counted; counts already read are kept.',
    },
    updateIdentity: {
        method: 'PATCH',
        path: '/api/settings/identity',
        body: ReconfigureIdentityBodySchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Reconfigure the IdP (provider swap, issuer, client ID/secret, group claim, role mappings). Replaces the active provider and revokes all sessions.',
    },
    updateGroupNameResolution: {
        method: 'PATCH',
        path: '/api/settings/identity/group-names',
        body: UpdateGroupNameResolutionSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Turn IdP group display-name resolution on or off (docs/10 §IdP group display names). Entra only; org_admin only; audited. Presentation-only — matching, role derivation, session claims, and X-User-Groups are unaffected, and no session is revoked.',
    },
    testIdentity: {
        method: 'POST',
        path: '/api/settings/identity/test',
        // Optional candidate configuration: the reconfigure form verifies what it is
        // about to save; an empty body tests the stored connection, whose client ID
        // and Key Vault secret the server reads itself. Reuses the install issuer
        // schema so the probe stays bounded to public HTTPS endpoints (SSRF
        // posture, docs/10) — never a generic URL.
        body: TestIdentityBodySchema.optional(),
        responses: {
            200: TestIdentityResultSchema,
            ...errorResponses,
        },
        summary: 'Run the install wizard\u2019s issuer + client-registration verification against a candidate configuration, or the stored one.',
    },
    verifySourceControl: {
        method: 'POST',
        path: '/api/settings/source-control/verify',
        body: z.object({}).optional(),
        responses: {
            200: SourceControlVerifyResultSchema,
            ...errorResponses,
        },
        summary: 'Check that the active GitHub App installation can reach every managed repo; lists any it cannot see.',
    },
    recreateGithubApp: {
        method: 'POST',
        path: '/api/settings/source-control/github-app/recreate',
        body: RecreateGithubAppBodySchema,
        responses: {
            200: RecreateGithubAppResultSchema,
            ...errorResponses,
        },
        summary: 'Re-run the GitHub App manifest flow to replace the existing App (rotates its vault secrets on callback; the pipeline callback token is preserved).',
    },
    previewScmMigration: {
        method: 'POST',
        path: '/api/settings/source-control/migration/preview',
        body: z.object({}).optional(),
        responses: {
            200: ScmMigrationPreviewSchema,
            ...errorResponses,
        },
        summary: 'Dry-run of an org migration: the pending target installation and exactly which repo coordinates will move. Changes nothing.',
    },
    executeScmMigration: {
        method: 'POST',
        path: '/api/settings/source-control/migration/execute',
        body: ExecuteScmMigrationBodySchema,
        responses: {
            200: ScmMigrationResultSchema,
            ...errorResponses,
        },
        summary: 'Complete an org migration after IT transfers the managed repos: verifies repo access under the target installation, then re-points all SCM coordinates in one transaction and re-verifies branch protection.',
    },
    bootstrapPluginMarketplace: {
        method: 'POST',
        path: '/api/settings/plugin-marketplace/bootstrap',
        body: BootstrapPluginMarketplaceBodySchema,
        responses: {
            200: PluginMarketplaceBootstrapResultSchema,
            ...errorResponses,
        },
        summary: 'Create a public or private customer-owned plugin marketplace repo (render the bundled template, create the repo, commit main, persist the row, audit) — or recreate that repository when it is missing on the SCM host. Idempotent by repository name and visibility. Org-admin only; gated on the GitHub App being connected.',
    },
    unregisterPluginMarketplace: {
        method: 'DELETE',
        path: '/api/settings/plugin-marketplace/:id',
        pathParams: PluginMarketplaceIdParamsSchema,
        body: c.noBody(),
        responses: {
            200: PluginMarketplaceUnregisterResultSchema,
            ...errorResponses,
        },
        summary: 'Unregister a plugin marketplace row (org-admin only): retires it from Settings/wizard listings, managed-repo verification, the migration move list, and the updater. The GitHub repository itself is never deleted or archived.',
    },
    updateNotifications: {
        method: 'PATCH',
        path: '/api/settings/notifications',
        body: UpdateNotificationsSettingsSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Update notification recipients, the Slack destination switch, and per-category routing (app health alerts, permission requests, connection requests, weekly digest). The Slack webhook URL is set on its own write-only route.',
    },
    setSlackWebhook: {
        method: 'PUT',
        path: '/api/settings/notifications/slack-webhook',
        body: SetSlackWebhookSchema,
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Register the Slack incoming-webhook URL notifications post to (IT; audited). Write-only: the URL is stored in the vault and no read ever returns it. Must be an https://hooks.slack.com/ URL.',
    },
    deleteSlackWebhook: {
        method: 'DELETE',
        path: '/api/settings/notifications/slack-webhook',
        body: c.noBody(),
        responses: {
            200: OrgSettingsSchema,
            ...errorResponses,
        },
        summary: 'Forget the Slack destination (IT; audited): removes the stored webhook URL and switches the destination off.',
    },
    updateAppLifecycle: {
        method: 'PATCH',
        path: '/api/settings/app-lifecycle',
        body: UpdateAppLifecycleSettingsSchema,
        responses: {
            200: AppLifecycleSettingsSchema,
            ...errorResponses,
        },
        summary: 'Turn the unused-app lifecycle on or off for the org, and set the day counts it runs on. Off means no app is classified, nobody is emailed, and nothing is paused; the merged day counts must stay strictly increasing.',
    },
});
//# sourceMappingURL=settings.js.map